/**
 * Horizon Predictions Table — Polygon Well Predictions tab.
 *
 * Mirrors Phreatic's Well Inventory layout: one row per (well, analyte) with
 * MCL / current concentration / per-horizon predictions, each horizon as its
 * own column. Sourced from the v3.2 timeline-predictions endpoint, which
 * returns six BTEX horizons (3mo → 20yr) and three TPH horizons (3mo → 2yr).
 *
 * TPH long horizons (5yr / 10yr / 20yr) render as em-dash because the v3.2
 * metadata flags those bundles `do_not_use`.
 */
import { useMemo } from 'react';
import type { TimelinePredictionsResponse, WellPredictionByMonth } from '../../api/polygon';

const HORIZONS = ['3mo', '1yr', '2yr', '5yr', '10yr', '20yr'] as const;
type Horizon = typeof HORIZONS[number];

interface HorizonPredictionsTableProps {
  data: TimelinePredictionsResponse | null;
  loading?: boolean;
  analyteFilter?: string;
}

interface PivotRow {
  well_id: string;
  well_name: string;
  analyte_code: string;
  analyte_group: string;
  mcl_mgl: number;
  current_conc_mgl: number | null;
  current_sample_date: string | null;
  current_is_non_detect: boolean | null;
  by_horizon: Partial<Record<Horizon, WellPredictionByMonth>>;
}

function formatConc(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v >= 1) return v.toFixed(2);
  if (v >= 0.01) return v.toFixed(3);
  if (v >= 1e-4) return v.toFixed(5);
  return v.toExponential(1);
}

function exceedanceClass(conc: number, mcl: number): string {
  if (!mcl || mcl <= 0) return 'text-mono-300';
  const ratio = conc / mcl;
  if (ratio >= 5) return 'text-red-400 font-semibold';
  if (ratio >= 1) return 'text-orange-400 font-semibold';
  if (ratio >= 0.5) return 'text-yellow-300';
  return 'text-emerald-300';
}

function trendBadge(trend: string): { text: string; cls: string } {
  if (trend === 'decreasing') return { text: '↓', cls: 'text-emerald-400' };
  if (trend === 'increasing') return { text: '↑', cls: 'text-red-400' };
  return { text: '→', cls: 'text-mono-400' };
}

// Map a horizon's `horizon_label` from the API to our Horizon enum. Falls back
// to inspecting horizon_days for robustness.
function detectHorizon(slice: { horizon_label?: string | null; horizon_days?: number | null }): Horizon | null {
  if (slice.horizon_label && (HORIZONS as readonly string[]).includes(slice.horizon_label)) {
    return slice.horizon_label as Horizon;
  }
  const d = slice.horizon_days ?? null;
  if (d === 90) return '3mo';
  if (d === 365) return '1yr';
  if (d === 730) return '2yr';
  if (d === 1825) return '5yr';
  if (d === 3650) return '10yr';
  if (d === 7300) return '20yr';
  return null;
}

export function HorizonPredictionsTable({ data, loading, analyteFilter }: HorizonPredictionsTableProps) {
  const rows = useMemo<PivotRow[]>(() => {
    if (!data?.months?.length) return [];
    const byKey = new Map<string, PivotRow>();

    for (const slice of data.months) {
      const horizon = detectHorizon(slice);
      if (!horizon) continue;
      for (const p of slice.predictions) {
        if (analyteFilter && analyteFilter !== 'all' && p.analyte_code !== analyteFilter) continue;
        const key = `${p.well_id}::${p.analyte_code}`;
        let row = byKey.get(key);
        if (!row) {
          row = {
            well_id: p.well_id,
            well_name: p.well_name,
            analyte_code: p.analyte_code,
            analyte_group: p.analyte_group,
            mcl_mgl: p.mcl_mgl,
            current_conc_mgl: p.current_conc_mgl ?? null,
            current_sample_date: p.current_sample_date ?? null,
            current_is_non_detect: p.current_is_non_detect ?? null,
            by_horizon: {},
          };
          byKey.set(key, row);
        }
        row.by_horizon[horizon] = p;
        // Keep latest MCL (in case it varies per row — shouldn't, but be safe).
        if (p.mcl_mgl && (!row.mcl_mgl || row.mcl_mgl === 0)) row.mcl_mgl = p.mcl_mgl;
        // Backfill the latest-sample fields on whichever slice carries them
        // (the backend attaches them per row but they're identical across
        // horizons for a given (well, analyte) — first non-null wins).
        if (row.current_conc_mgl == null && p.current_conc_mgl != null) {
          row.current_conc_mgl = p.current_conc_mgl;
          row.current_sample_date = p.current_sample_date ?? null;
          row.current_is_non_detect = p.current_is_non_detect ?? null;
        }
      }
    }

    // Numeric-aware well sort, then analyte alphabetical.
    return Array.from(byKey.values()).sort((a, b) => {
      const numA = parseInt(a.well_name.replace(/\D/g, ''), 10);
      const numB = parseInt(b.well_name.replace(/\D/g, ''), 10);
      const aHas = !isNaN(numA);
      const bHas = !isNaN(numB);
      if (aHas && bHas && numA !== numB) return numA - numB;
      const wellCmp = a.well_name.localeCompare(b.well_name);
      if (wellCmp !== 0) return wellCmp;
      return a.analyte_code.localeCompare(b.analyte_code);
    });
  }, [data, analyteFilter]);

  if (loading) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center text-mono-500 text-sm">
        Loading horizon predictions…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center text-mono-500 text-sm">
        No horizon predictions available for this site.
      </div>
    );
  }

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-mono-400 border-b border-mono-800 bg-mono-950/50">
            <th className="text-left py-2 px-3 sticky left-0 bg-mono-950 z-10">Well</th>
            <th className="text-left py-2 px-2">Analyte</th>
            <th className="text-right py-2 px-2">MCL (mg/L)</th>
            <th className="text-right py-2 px-2 text-amber-300/80 uppercase tracking-wider">Current</th>
            {HORIZONS.map(h => (
              <th key={h} className="text-right py-2 px-2 text-cyan-500/70 uppercase tracking-wider">
                {h}
              </th>
            ))}
            <th className="text-center py-2 px-2">Trend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const tr = (() => {
              for (const h of HORIZONS) {
                const p = row.by_horizon[h];
                if (p) return trendBadge(p.trend);
              }
              return { text: '—', cls: 'text-mono-500' };
            })();

            return (
              <tr
                key={`${row.well_id}::${row.analyte_code}`}
                className="border-b border-mono-800/50 hover:bg-mono-800/30"
              >
                <td className="py-2 px-3 text-mono-200 font-medium sticky left-0 bg-mono-900">
                  {row.well_name}
                </td>
                <td className="py-2 px-2 text-mono-300">
                  {row.analyte_code}
                  <span className="ml-1 text-[9px] uppercase tracking-wider text-mono-600">
                    {row.analyte_group}
                  </span>
                </td>
                <td className="text-right py-2 px-2 text-mono-400">
                  {row.mcl_mgl ? row.mcl_mgl.toFixed(3) : '—'}
                </td>
                <td
                  className={`text-right py-2 px-2 ${
                    row.current_conc_mgl == null
                      ? 'text-mono-700'
                      : row.current_is_non_detect
                      ? 'text-mono-500 italic'
                      : exceedanceClass(row.current_conc_mgl, row.mcl_mgl)
                  }`}
                  title={
                    row.current_conc_mgl == null
                      ? 'No analytical sample on file for this well + analyte'
                      : `${row.current_sample_date ?? 'unknown date'}\n` +
                        `${row.current_is_non_detect ? 'ND (½ DL): ' : 'detect: '}${formatConc(row.current_conc_mgl)} mg/L`
                  }
                >
                  {row.current_conc_mgl == null ? '—' : formatConc(row.current_conc_mgl)}
                  {row.current_is_non_detect && row.current_conc_mgl != null && (
                    <span className="ml-1 text-[8px] uppercase tracking-wider opacity-60">nd</span>
                  )}
                </td>
                {HORIZONS.map(h => {
                  const p = row.by_horizon[h];
                  if (!p) {
                    // TPH long horizons get a hint about the do-not-use status.
                    const isLongTph =
                      row.analyte_group === 'tph' && (h === '5yr' || h === '10yr' || h === '20yr');
                    return (
                      <td
                        key={h}
                        className="text-right py-2 px-2 text-mono-700"
                        title={isLongTph ? 'v3.2 TPH model not trained beyond 2yr horizon' : 'No prediction'}
                      >
                        {isLongTph ? '·' : '—'}
                      </td>
                    );
                  }
                  return (
                    <td
                      key={h}
                      className={`text-right py-2 px-2 ${exceedanceClass(p.predicted_conc_mgl, row.mcl_mgl)}`}
                      title={
                        `${p.target_date}\n` +
                        `pred: ${formatConc(p.predicted_conc_mgl)} mg/L\n` +
                        (p.ci_90_lower_mgl != null && p.ci_90_upper_mgl != null
                          ? `90% CI: ${formatConc(p.ci_90_lower_mgl)} – ${formatConc(p.ci_90_upper_mgl)} mg/L\n`
                          : '') +
                        `P(exceed MCL): ${(p.exceedance_probability * 100).toFixed(0)}%`
                      }
                    >
                      {formatConc(p.predicted_conc_mgl)}
                    </td>
                  );
                })}
                <td className={`text-center py-2 px-2 ${tr.cls}`}>{tr.text}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
